import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { formatPrice } from '../../utils/format';

const PERIODS = [
  { value: '7d',  label: 'Últimos 7 días' },
  { value: '30d', label: 'Últimos 30 días' },
  { value: '90d', label: 'Últimos 90 días' },
  { value: 'all', label: 'Todo el tiempo' },
];

const StatCard = ({ label, value, sub }) => (
  <div style={{
    background: '#fff',
    border: '1px solid #e5e5e5',
    borderRadius: 12,
    padding: '1.25rem 1.5rem',
    minWidth: 140,
    flex: '1 1 140px',
  }}>
    <div style={{ fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#888', marginBottom: '0.4rem' }}>
      {label}
    </div>
    <div style={{ fontSize: '1.65rem', fontWeight: 700, color: '#1a1a1a', lineHeight: 1 }}>
      {value}
    </div>
    {sub && <div style={{ fontSize: '0.78rem', color: '#aaa', marginTop: '0.3rem' }}>{sub}</div>}
  </div>
);

const TABS = [
  { id: 'artists',   label: 'Artistas' },
  { id: 'products',  label: 'Productos' },
  { id: 'allies',    label: 'Aliados' },
  { id: 'languages', label: 'Idiomas' },
  { id: 'platforms', label: 'Plataformas' },
];

const LANGUAGE_DISPLAY = {
  es: { flag: '🇲🇽', name: 'Español' },
  en: { flag: '🇺🇸', name: 'English' },
  zh: { flag: '🇨🇳', name: '中文' },
  ja: { flag: '🇯🇵', name: '日本語' },
};

const PLATFORM_DISPLAY = {
  web: { icon: '🌐', name: 'Web' },
  ios: { icon: '📱', name: 'iOS' },
};

const TTL_MS = 5 * 60 * 1000;
const metricsCache = {};

const AdminArtistMetricsPage = () => {
  const { session } = useAuth();
  const [period, setPeriod]     = useState('30d');
  const [tab, setTab]           = useState('artists');
  const [data, setData]         = useState(() => metricsCache['30d']?.data ?? null);
  const [loading, setLoading]   = useState(!metricsCache['30d']);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError]       = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async (p) => {
    if (!session?.access_token) return;
    const cached = metricsCache[p];
    const isFresh = cached && Date.now() - cached.ts < TTL_MS;
    if (isFresh) {
      setData(cached.data);
      setLoading(false);
      return;
    }
    if (cached) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/admin/artist-metrics?period=${p}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Error al cargar métricas');
      metricsCache[p] = { data: json.data, ts: Date.now() };
      setData(json.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [session]);

  useEffect(() => { load(period); }, [load, period]);

  const fmt = (n) => Number(n || 0).toLocaleString('es-MX');
  const fmtMXN = (n) => formatPrice(n || 0, 'MXN', 'es-MX');

  return (
    <div className="container" style={{ padding: '2rem 0 5rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', marginBottom: '2rem' }}>
        <div>
          <Link to="/admin" style={{ fontSize: '0.82rem', color: '#888', textDecoration: 'none' }}>
            ← Panel
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.25rem' }}>
            <h1 style={{ fontFamily: "'Gambado Sans', sans-serif", fontSize: '1.75rem', color: '#333', margin: 0 }}>
              Métricas
            </h1>
            {refreshing && (
              <span style={{ fontSize: '0.75rem', color: '#aaa' }}>Actualizando…</span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button
            onClick={() => { delete metricsCache[period]; load(period); }}
            disabled={loading || refreshing}
            style={{ fontSize: '0.8rem', color: '#888', background: 'none', border: '1px solid #e5e5e5', borderRadius: 6, padding: '0.35rem 0.7rem', cursor: 'pointer' }}
          >
            ↻ Actualizar
          </button>

        {/* Period selector */}
        <div style={{ display: 'flex', gap: '0.4rem', background: '#f5f5f5', borderRadius: 8, padding: '0.3rem' }}>
          {PERIODS.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setPeriod(value)}
              style={{
                padding: '0.35rem 0.85rem',
                borderRadius: 6,
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: 600,
                background: period === value ? '#1a1a1a' : 'transparent',
                color:      period === value ? '#fff'    : '#555',
                transition: 'background 0.15s ease',
              }}
            >
              {label}
            </button>
          ))}
        </div>
        </div>
      </div>

      {loading && !data && (
        <p style={{ color: '#888', textAlign: 'center', padding: '3rem 0' }}>Cargando métricas…</p>
      )}
      {error && (
        <p style={{ color: '#dc3545', textAlign: 'center', padding: '3rem 0' }}>{error}</p>
      )}

      {data && (
        <>
          {/* Summary cards */}
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '2rem' }}>
            <StatCard label="Visitas artistas" value={fmt(data.totals.pageViews)} />
            <StatCard label="Vistas desde kiosco" value={fmt(data.totals.kioskPageViews)} />
            <StatCard label="Clics en productos" value={fmt(data.totals.productClicks)} />
            <StatCard label="Contactos recibidos" value={fmt(data.totals.contactSubmissions)} />
            <StatCard label="Ingresos atribuidos" value={fmtMXN(data.totals.revenue)} sub={`${fmt(data.totals.unitsSold)} unidades`} />
          </div>

          {/* Tab bar */}
          <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e5e5e5', marginBottom: '1.5rem' }}>
            {TABS.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => { setTab(id); setExpanded(null); }}
                style={{
                  padding: '0.55rem 1.25rem',
                  border: 'none',
                  borderBottom: tab === id ? '2px solid #1a1a1a' : '2px solid transparent',
                  marginBottom: '-2px',
                  background: 'none',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: tab === id ? 700 : 500,
                  color: tab === id ? '#1a1a1a' : '#888',
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* ── Artistas tab ── */}
          {tab === 'artists' && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e5e5' }}>
                    {['Artista', 'Visitas', 'Vistas kiosco', 'Clics producto', 'Contactos', 'Ingresos', 'Unidades'].map((h) => (
                      <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: h === 'Artista' ? 'left' : 'right', fontWeight: 700, color: '#555', fontSize: '0.75rem', letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                        {h}
                      </th>
                    ))}
                    <th style={{ width: 80 }} />
                  </tr>
                </thead>
                <tbody>
                  {data.artists.map((artist) => {
                    const hasActivity = artist.pageViews + artist.productClicks + artist.revenue > 0;
                    const isExpanded  = expanded === artist.id;
                    const hasLanguages = (artist.languages?.total || 0) > 0;
                    const canExpand = artist.topProducts.length > 0 || hasLanguages;
                    return (
                      <>
                        <tr key={artist.id} style={{ borderBottom: '1px solid #f0f0f0', background: isExpanded ? '#fafaf7' : 'transparent', opacity: hasActivity ? 1 : 0.45 }}>
                          <td style={{ padding: '0.7rem 0.75rem' }}>
                            <Link to={`/admin/artists/${artist.id}`} style={{ fontWeight: 600, color: '#7c3aed', textDecoration: 'none' }}>
                              {artist.name || artist.slug}
                            </Link>
                            {artist.slug && <span style={{ display: 'block', fontSize: '0.72rem', color: '#aaa' }}>/artist/{artist.slug}</span>}
                          </td>
                          <td style={{ padding: '0.7rem 0.75rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(artist.pageViews)}</td>
                          <td style={{ padding: '0.7rem 0.75rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(artist.kioskPageViews)}</td>
                          <td style={{ padding: '0.7rem 0.75rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(artist.productClicks)}</td>
                          <td style={{ padding: '0.7rem 0.75rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(artist.contactSubmissions)}</td>
                          <td style={{ padding: '0.7rem 0.75rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: artist.revenue > 0 ? '#1a1a1a' : '#ccc' }}>{fmtMXN(artist.revenue)}</td>
                          <td style={{ padding: '0.7rem 0.75rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(artist.unitsSold)}</td>
                          <td style={{ padding: '0.7rem 0.75rem', textAlign: 'center' }}>
                            {canExpand && (
                              <button onClick={() => setExpanded(isExpanded ? null : artist.id)} style={{ fontSize: '0.75rem', background: 'none', border: '1px solid #ddd', borderRadius: 4, padding: '0.2rem 0.5rem', cursor: 'pointer', color: '#555' }}>
                                {isExpanded ? '▲' : '▼'}
                              </button>
                            )}
                          </td>
                        </tr>
                        {isExpanded && canExpand && (
                          <tr key={`${artist.id}-detail`} style={{ background: '#fafaf7' }}>
                            <td colSpan={8} style={{ padding: '0 0.75rem 1rem 2.5rem' }}>
                              {artist.topProducts.length > 0 && (
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', marginBottom: hasLanguages ? '1rem' : 0 }}>
                                  <thead>
                                    <tr style={{ borderBottom: '1px solid #e5e5e5' }}>
                                      {['Producto', 'Clics', 'Ingresos', 'Unidades'].map((h) => (
                                        <th key={h} style={{ padding: '0.4rem 0.5rem', textAlign: h === 'Producto' ? 'left' : 'right', color: '#888', fontWeight: 600 }}>{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {artist.topProducts.map((p) => (
                                      <tr key={p.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                        <td style={{ padding: '0.4rem 0.5rem', color: '#333' }}>{p.name}</td>
                                        <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>{fmt(p.clicks)}</td>
                                        <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', fontWeight: 600 }}>{fmtMXN(p.revenue)}</td>
                                        <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>{fmt(p.units)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                              {hasLanguages && (
                                <div>
                                  <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#888', marginBottom: '0.5rem' }}>
                                    Idioma de las visitas
                                  </div>
                                  <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
                                    {artist.languages.rows.filter((r) => r.views > 0).map((row) => {
                                      const display = LANGUAGE_DISPLAY[row.code] || { flag: '', name: row.code.toUpperCase() };
                                      return (
                                        <div key={row.code} style={{ fontSize: '0.82rem', color: '#333' }}>
                                          <span aria-hidden="true" style={{ marginRight: '0.35rem' }}>{display.flag}</span>
                                          {display.name}: <strong>{fmt(row.views)}</strong>
                                          <span style={{ color: '#aaa' }}> ({row.share}%)</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
              {data.artists.length === 0 && <p style={{ textAlign: 'center', color: '#aaa', padding: '3rem 0' }}>No hay artistas registrados.</p>}
            </div>
          )}

          {/* ── Productos tab ── */}
          {tab === 'products' && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e5e5' }}>
                    {['Producto', 'Visitas', 'Clics', 'Carrito', 'Ingresos', 'Unidades'].map((h) => (
                      <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: h === 'Producto' ? 'left' : 'right', fontWeight: 700, color: '#555', fontSize: '0.75rem', letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.products.map((p) => {
                    const hasActivity = p.pageViews + p.cardClicks + p.revenue > 0;
                    return (
                      <tr key={p.id} style={{ borderBottom: '1px solid #f0f0f0', opacity: hasActivity ? 1 : 0.45 }}>
                        <td style={{ padding: '0.7rem 0.75rem' }}>
                          <Link to={`/product/${p.slug || p.id}`} style={{ fontWeight: 600, color: '#2ca9e1', textDecoration: 'none' }}>{p.name}</Link>
                          <span style={{ display: 'block', fontSize: '0.72rem', color: '#aaa' }}>{p.artist}{p.isSoldOut ? ' · Agotado' : ''}</span>
                        </td>
                        <td style={{ padding: '0.7rem 0.75rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(p.pageViews)}</td>
                        <td style={{ padding: '0.7rem 0.75rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(p.cardClicks)}</td>
                        <td style={{ padding: '0.7rem 0.75rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(p.addToCarts)}</td>
                        <td style={{ padding: '0.7rem 0.75rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: p.revenue > 0 ? '#1a1a1a' : '#ccc' }}>{fmtMXN(p.revenue)}</td>
                        <td style={{ padding: '0.7rem 0.75rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(p.unitsSold)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {data.products.length === 0 && <p style={{ textAlign: 'center', color: '#aaa', padding: '3rem 0' }}>No hay productos registrados.</p>}
            </div>
          )}

          {/* ── Aliados tab ── */}
          {tab === 'allies' && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e5e5' }}>
                    {['Aliado', 'Visitas', 'Carrito', 'Ingresos', 'Unidades'].map((h) => (
                      <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: h === 'Aliado' ? 'left' : 'right', fontWeight: 700, color: '#555', fontSize: '0.75rem', letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                    <th style={{ width: 80 }} />
                  </tr>
                </thead>
                <tbody>
                  {data.allies.map((a) => {
                    const hasActivity = a.pageViews + a.addToCarts + a.revenue > 0;
                    const isExpanded  = expanded === a.id;
                    return (
                      <>
                        <tr key={a.id} style={{ borderBottom: '1px solid #f0f0f0', background: isExpanded ? '#fafaf7' : 'transparent', opacity: hasActivity ? 1 : 0.45 }}>
                          <td style={{ padding: '0.7rem 0.75rem' }}>
                            <Link to={`/ally/${a.slug || a.id}`} style={{ fontWeight: 600, color: '#f59e0b', textDecoration: 'none' }}>{a.name}</Link>
                            {a.slug && <span style={{ display: 'block', fontSize: '0.72rem', color: '#aaa' }}>/ally/{a.slug}</span>}
                          </td>
                          <td style={{ padding: '0.7rem 0.75rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(a.pageViews)}</td>
                          <td style={{ padding: '0.7rem 0.75rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(a.addToCarts)}</td>
                          <td style={{ padding: '0.7rem 0.75rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: a.revenue > 0 ? '#1a1a1a' : '#ccc' }}>{fmtMXN(a.revenue)}</td>
                          <td style={{ padding: '0.7rem 0.75rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(a.unitsSold)}</td>
                          <td style={{ padding: '0.7rem 0.75rem', textAlign: 'center' }}>
                            {a.topProducts.length > 0 && (
                              <button onClick={() => setExpanded(isExpanded ? null : a.id)} style={{ fontSize: '0.75rem', background: 'none', border: '1px solid #ddd', borderRadius: 4, padding: '0.2rem 0.5rem', cursor: 'pointer', color: '#555' }}>
                                {isExpanded ? '▲' : '▼'}
                              </button>
                            )}
                          </td>
                        </tr>
                        {isExpanded && a.topProducts.length > 0 && (
                          <tr key={`${a.id}-detail`} style={{ background: '#fafaf7' }}>
                            <td colSpan={6} style={{ padding: '0 0.75rem 1rem 2.5rem' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                                <thead>
                                  <tr style={{ borderBottom: '1px solid #e5e5e5' }}>
                                    {['Producto', 'Carrito', 'Ingresos', 'Unidades'].map((h) => (
                                      <th key={h} style={{ padding: '0.4rem 0.5rem', textAlign: h === 'Producto' ? 'left' : 'right', color: '#888', fontWeight: 600 }}>{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {a.topProducts.map((p) => (
                                    <tr key={p.id} style={{ borderBottom: '1px solid #f0f0f0', opacity: p.isActive ? 1 : 0.5 }}>
                                      <td style={{ padding: '0.4rem 0.5rem', color: '#333' }}>{p.name}{!p.isActive ? ' · Inactivo' : ''}</td>
                                      <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>{fmt(p.addToCarts)}</td>
                                      <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', fontWeight: 600 }}>{fmtMXN(p.revenue)}</td>
                                      <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>{fmt(p.units)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
              {data.allies.length === 0 && <p style={{ textAlign: 'center', color: '#aaa', padding: '3rem 0' }}>No hay aliados registrados.</p>}

              {/* Catálogo de productos de aliados (flat) */}
              <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#333', margin: '2.5rem 0 1rem' }}>
                Catálogo de productos de aliados
              </h2>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e5e5' }}>
                    {['Producto', 'Aliado', 'Precio', 'Carrito', 'Ingresos', 'Unidades'].map((h) => (
                      <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: h === 'Producto' || h === 'Aliado' ? 'left' : 'right', fontWeight: 700, color: '#555', fontSize: '0.75rem', letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(data.allyProducts || []).map((p) => {
                    const hasActivity = p.addToCarts + p.revenue > 0;
                    return (
                      <tr key={p.id} style={{ borderBottom: '1px solid #f0f0f0', opacity: hasActivity ? 1 : 0.45 }}>
                        <td style={{ padding: '0.7rem 0.75rem', color: '#333' }}>
                          {p.name}{!p.isActive ? ' · Inactivo' : ''}
                        </td>
                        <td style={{ padding: '0.7rem 0.75rem' }}>
                          <Link to={`/ally/${p.allySlug || p.allyId}`} style={{ color: '#f59e0b', textDecoration: 'none' }}>{p.allyName}</Link>
                        </td>
                        <td style={{ padding: '0.7rem 0.75rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMXN(p.price)}</td>
                        <td style={{ padding: '0.7rem 0.75rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(p.addToCarts)}</td>
                        <td style={{ padding: '0.7rem 0.75rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: p.revenue > 0 ? '#1a1a1a' : '#ccc' }}>{fmtMXN(p.revenue)}</td>
                        <td style={{ padding: '0.7rem 0.75rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(p.unitsSold)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {(data.allyProducts || []).length === 0 && <p style={{ textAlign: 'center', color: '#aaa', padding: '3rem 0' }}>No hay productos de aliados registrados.</p>}
            </div>
          )}

          {/* ── Idiomas tab ── */}
          {tab === 'languages' && (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
                <StatCard label="Vistas con idioma detectado" value={fmt(data.languages.totalPageViews)} />
                <StatCard label="Cambios de idioma" value={fmt(data.languages.totalSwitches)} sub="clics en el selector" />
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e5e5' }}>
                    {['Idioma', 'Vistas activas', '% del tráfico', 'Cambios hacia'].map((h) => (
                      <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: h === 'Idioma' ? 'left' : 'right', fontWeight: 700, color: '#555', fontSize: '0.75rem', letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.languages.rows.map((row) => {
                    const display = LANGUAGE_DISPLAY[row.code] || { flag: '', name: row.code.toUpperCase() };
                    const hasActivity = row.activePageViews + row.switchesTo > 0;
                    return (
                      <tr key={row.code} style={{ borderBottom: '1px solid #f0f0f0', opacity: hasActivity ? 1 : 0.45 }}>
                        <td style={{ padding: '0.7rem 0.75rem', fontWeight: 600, color: '#333' }}>
                          <span aria-hidden="true" style={{ marginRight: '0.5rem' }}>{display.flag}</span>
                          {display.name}
                        </td>
                        <td style={{ padding: '0.7rem 0.75rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(row.activePageViews)}</td>
                        <td style={{ padding: '0.7rem 0.75rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{row.activeShare}%</td>
                        <td style={{ padding: '0.7rem 0.75rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(row.switchesTo)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p style={{ fontSize: '0.78rem', color: '#aaa', marginTop: '1rem' }}>
                "Vistas activas" cuenta las páginas vistas mientras el sitio estaba en ese idioma (excluye /admin).
                "Cambios hacia" cuenta clics en el selector de idioma cuyo destino fue ese idioma, en el período seleccionado.
              </p>
            </div>
          )}

          {/* ── Plataformas tab ── */}
          {tab === 'platforms' && (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
                <StatCard label="Vistas totales" value={fmt(data.platforms.totalPageViews)} />
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e5e5' }}>
                    {['Plataforma', 'Vistas', '% del tráfico'].map((h) => (
                      <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: h === 'Plataforma' ? 'left' : 'right', fontWeight: 700, color: '#555', fontSize: '0.75rem', letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.platforms.rows.map((row) => {
                    const display = PLATFORM_DISPLAY[row.code] || { icon: '', name: row.code };
                    const hasActivity = row.pageViews > 0;
                    return (
                      <tr key={row.code} style={{ borderBottom: '1px solid #f0f0f0', opacity: hasActivity ? 1 : 0.45 }}>
                        <td style={{ padding: '0.7rem 0.75rem', fontWeight: 600, color: '#333' }}>
                          <span aria-hidden="true" style={{ marginRight: '0.5rem' }}>{display.icon}</span>
                          {display.name}
                        </td>
                        <td style={{ padding: '0.7rem 0.75rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(row.pageViews)}</td>
                        <td style={{ padding: '0.7rem 0.75rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{row.share}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p style={{ fontSize: '0.78rem', color: '#aaa', marginTop: '1rem' }}>
                Cuenta las páginas vistas por plataforma (excluye /admin). "iOS" son las vistas reportadas por la app nativa; todo lo demás se cuenta como "Web".
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminArtistMetricsPage;
